import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Download,
  Hash,
  Link as LinkIcon,
  Pencil,
  Phone,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { hasMinRole, useAuthStore } from "@/features/auth/store";
import { useGuardedAction } from "@/features/auth/access";
import { agentsApi } from "@/features/agents/api";
import { assistantsApi } from "@/features/assistants/api";
import { providersApi } from "@/features/settings/api";
import {
  phoneNumbersApi,
  messagingSettingsApi,
  type ExistingNumber,
  type PhoneNumber,
} from "@/features/telephony/api";
import { cn } from "@/lib/utils";

const KEY = ["phone-numbers"] as const;

// The server's reason as text. Handlers answer {error: "…"}, but limits,
// throttling and validation answer {error: {code, message, …}}; rendering
// that object blanks the page.
function errorText(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: unknown } })?.response?.data as
    | { error?: unknown; number?: unknown; detail?: unknown }
    | undefined;
  const error = data?.error;
  if (typeof error === "string" && error) return error;
  if (error && typeof error === "object") {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message) return message;
  }
  if (Array.isArray(data?.number) && typeof data.number[0] === "string") return data.number[0];
  if (typeof data?.detail === "string" && data.detail) return data.detail;
  return fallback;
}

export default function PhoneNumbersPage() {
  const role = useAuthStore((s) => s.user?.role ?? "member");
  const isAdmin = hasMinRole(role, "admin");
  const isCustomer = role === "customer";
  // A customer manages the numbers they were given — pointing one at their own
  // assistant is the whole reason they're on this page. Buying, releasing and
  // webhooks stay with the tenant.
  const canAssign = isAdmin || isCustomer;
  const guard = useGuardedAction();
  const qc = useQueryClient();

  const numbersQ = useQuery({
    queryKey: [...KEY, "list"],
    queryFn: phoneNumbersApi.list,
    staleTime: 15_000,
  });
  const agentsQ = useQuery({
    queryKey: ["agents", "list"],
    queryFn: agentsApi.list,
    staleTime: 60_000,
  });
  const assistantsQ = useQuery({
    queryKey: ["assistants"],
    queryFn: assistantsApi.list,
    staleTime: 60_000,
  });

  const numbers = numbersQ.data ?? [];
  const agents = agentsQ.data ?? [];
  const assistants = assistantsQ.data ?? [];

  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<PhoneNumber | null>(null);
  const [webhookTarget, setWebhookTarget] = useState<PhoneNumber | null>(null);
  const [releaseTarget, setReleaseTarget] = useState<PhoneNumber | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PhoneNumber | null>(null);

  function invalidate() {
    qc.invalidateQueries({ queryKey: [...KEY, "list"] });
  }

  const releaseM = useMutation({
    mutationFn: (id: string) => phoneNumbersApi.release(id),
    onSuccess: () => {
      toast.success("Number released");
      invalidate();
    },
    onError: (err) => {
      toast.error(errorText(err, "Failed to release number."));
    },
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => phoneNumbersApi.remove(id),
    onSuccess: () => {
      toast.success("Number removed");
      setDeleteTarget(null);
      invalidate();
    },
    onError: (err) => {
      // The server has the last word on whether a row still has call history.
      toast.error(errorText(err, "Failed to remove this number."));
      setDeleteTarget(null);
    },
  });

  function agentName(id?: string | null) {
    if (!id) return null;
    return agents.find((a) => a.id === id)?.name ?? null;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Phone Numbers</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your phone numbers for inbound and outbound calls.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => guard(() => setAddOpen(true))}>
            <Plus className="size-4" />
            Add Number
          </Button>
        )}
      </div>

      {isAdmin && <MessagingSenderCard />}

      {numbersQ.isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      )}

      {!numbersQ.isLoading && numbers.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <Phone className="size-10 text-muted-foreground" />
            <p className="text-base font-medium">No Phone Numbers</p>
            <p className="text-sm text-muted-foreground">
              Add a phone number to start receiving calls.
            </p>
          </CardContent>
        </Card>
      )}

      {!numbersQ.isLoading && numbers.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {numbers.map((n) => (
            <NumberCard
              key={n.id}
              n={n}
              isAdmin={isAdmin}
              canAssign={canAssign}
              agentName={n.inbound_agent_name || agentName(n.inbound_agent)}
              assistantName={
                n.inbound_assistant_name ||
                assistants.find((a) => a.id === n.inbound_assistant)?.name ||
                null
              }
              onEdit={() => setEditTarget(n)}
              onWebhook={() => setWebhookTarget(n)}
              onRelease={() => setReleaseTarget(n)}
              onDelete={() => setDeleteTarget(n)}
            />
          ))}
        </div>
      )}

      {addOpen && (
        <AddNumberDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          onChanged={invalidate}
        />
      )}
      <AssignAgentDialog
        target={editTarget}
        agents={agents}
        assistants={assistants}
        onClose={() => setEditTarget(null)}
        onSaved={invalidate}
      />
      <WebhookInfoDialog
        target={webhookTarget}
        onClose={() => setWebhookTarget(null)}
      />
      <AlertDialog
        open={!!releaseTarget}
        onOpenChange={(v) => !v && setReleaseTarget(null)}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Release phone number</AlertDialogTitle>
            <AlertDialogDescription>
              Release{" "}
              <span className="font-medium text-foreground">
                {releaseTarget?.formatted_number || releaseTarget?.number}
              </span>
              ? This removes it from your account. Cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={releaseM.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={releaseM.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (releaseTarget) releaseM.mutate(releaseTarget.id);
                setReleaseTarget(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {releaseM.isPending ? "Releasing…" : "Release"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this number</AlertDialogTitle>
            <AlertDialogDescription>
              Remove{" "}
              <span className="font-medium text-foreground">
                {deleteTarget?.formatted_number || deleteTarget?.number}
              </span>{" "}
              from the list? It has never taken a call, so nothing in your call
              log refers to it.
              {deleteTarget?.status !== "released" && (
                <>
                  {" "}
                  It is still marked active
                  {deleteTarget?.provider === "twilio" ||
                  deleteTarget?.provider === "telnyx"
                    ? ` — release it first if you don't want to keep paying ${
                        deleteTarget?.provider === "twilio" ? "Twilio" : "Telnyx"
                      } for it.`
                    : "."}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteM.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteM.isPending}
              onClick={(e) => {
                // Keep the dialog up while the request runs — the server can
                // still refuse if a call landed in the meantime.
                e.preventDefault();
                if (deleteTarget) deleteM.mutate(deleteTarget.id);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteM.isPending ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MessagingSenderCard() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["tenant", "messaging-settings"],
    queryFn: messagingSettingsApi.get,
    staleTime: 60_000,
    retry: false,
  });
  const [senderId, setSenderId] = useState("");
  const [profileId, setProfileId] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (q.data && !dirty) {
      setSenderId(q.data.sms_sender_id ?? "");
      setProfileId(q.data.messaging_profile_id ?? "");
    }
  }, [q.data, dirty]);

  const save = useMutation({
    mutationFn: () =>
      messagingSettingsApi.update({
        sms_sender_id: senderId.trim(),
        messaging_profile_id: profileId.trim(),
      }),
    onSuccess: () => {
      toast.success("Messaging settings saved");
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["tenant", "messaging-settings"] });
    },
    onError: (err) => {
      const e = err as {
        response?: {
          data?: { sms_sender_id?: string; messaging_profile_id?: string };
        };
      };
      toast.error(
        e?.response?.data?.sms_sender_id ||
          e?.response?.data?.messaging_profile_id ||
          "Failed to save messaging settings.",
      );
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">SMS follow-up sender</CardTitle>
        <CardDescription>
          Controls the &quot;from&quot; on outbound follow-up texts. Leave both
          blank to send from your number (fine for domestic SMS). For
          international routes (e.g. Pakistan) carriers require an{" "}
          <strong>alphanumeric sender ID</strong> — and when you use one, Telnyx
          also needs the <strong>messaging profile ID</strong> it belongs to.
          Both must be pre-registered/approved in Telnyx for the destination
          country, or messages are still blocked.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="sms-sender">Alphanumeric sender ID</Label>
            <Input
              id="sms-sender"
              value={senderId}
              maxLength={11}
              placeholder="e.g. Chawowa (max 11 letters/numbers)"
              onChange={(e) => {
                setSenderId(e.target.value);
                setDirty(true);
              }}
              disabled={q.isLoading}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="msg-profile">Messaging profile ID</Label>
            <Input
              id="msg-profile"
              value={profileId}
              placeholder="Telnyx → Messaging → your profile (UUID)"
              onChange={(e) => {
                setProfileId(e.target.value);
                setDirty(true);
              }}
              disabled={q.isLoading}
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            onClick={() => save.mutate()}
            disabled={save.isPending || q.isLoading || !dirty}
          >
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function NumberCard({
  n,
  isAdmin,
  canAssign,
  agentName,
  assistantName,
  onEdit,
  onWebhook,
  onRelease,
  onDelete,
}: {
  n: PhoneNumber;
  isAdmin: boolean;
  canAssign: boolean;
  agentName: string | null;
  assistantName: string | null;
  onEdit: () => void;
  onWebhook: () => void;
  onRelease: () => void;
  onDelete: () => void;
}) {
  const calls = n.linked_calls ?? n.total_calls ?? 0;
  return (
    <Card>
      <CardHeader className="space-y-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Phone className="size-5 text-indigo-500" />
            <CardTitle className="font-mono text-base">
              {n.formatted_number || n.number}
            </CardTitle>
          </div>
          {canAssign && (
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={onEdit}
                aria-label="Assign agent"
              >
                <Pencil className="size-4" />
              </Button>
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={onWebhook}
                  aria-label="Webhook setup"
                >
                  <LinkIcon className="size-4" />
                </Button>
              )}
              {isAdmin && n.status !== "released" && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 text-destructive hover:text-destructive"
                  onClick={onRelease}
                  aria-label="Release number"
                  title="Release this number back to the carrier"
                >
                  <Trash2 className="size-4" />
                </Button>
              )}
              {/* Removing the row is separate from releasing the number, and
                  only offered while nothing in the call log points at it. */}
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 text-muted-foreground hover:text-destructive disabled:opacity-40"
                  onClick={onDelete}
                  disabled={n.can_delete === false}
                  aria-label="Remove from list"
                  title={
                    n.can_delete === false
                      ? `Kept because ${calls} call${calls === 1 ? "" : "s"} in the log happened on this number`
                      : "Remove this number from the list"
                  }
                >
                  <X className="size-4" />
                </Button>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <span
            className={cn(
              "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize",
              n.status === "active"
                ? "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400"
                : "text-muted-foreground bg-muted",
            )}
          >
            {n.status}
          </span>
          <span
            className={cn(
              "inline-flex items-center rounded-full border px-2 py-0.5 text-xs capitalize",
              n.provider === "twilio"
                ? "border-indigo-200 text-indigo-700 dark:border-indigo-900 dark:text-indigo-400"
                : n.provider === "telnyx"
                ? "border-emerald-200 text-emerald-700 dark:border-emerald-900 dark:text-emerald-400"
                : "border-input text-muted-foreground",
            )}
          >
            {n.provider === "twilio"
              ? "Twilio"
              : n.provider === "telnyx"
              ? "Telnyx"
              : n.provider === "imported"
              ? "Imported"
              : n.provider || "Custom"}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        <p>
          <span className="text-muted-foreground">Answered by:</span>{" "}
          {assistantName || agentName ? (
            <span className="inline-flex items-center gap-1.5">
              {assistantName || agentName}
              <span
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                  assistantName
                    ? "bg-violet-50 text-violet-600 dark:bg-violet-950/40 dark:text-violet-400"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {assistantName ? "Assistant" : "Agent"}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">No one assigned</span>
          )}
        </p>
        <p className="text-muted-foreground">
          {calls} call{calls === 1 ? "" : "s"}
        </p>
      </CardContent>
    </Card>
  );
}

function AddNumberDialog({
  open,
  onOpenChange,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onChanged: () => void;
}) {
  const [tab, setTab] = useState<"import" | "buy" | "custom">("import");
  const [error, setError] = useState("");

  function handleSuccess() {
    setError("");
    onOpenChange(false);
    onChanged();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Phone Number</DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
          <TabsList className="flex w-full">
            <TabsTrigger value="import" className="flex-1">
              <Download className="size-3.5" />
              Import Twilio
            </TabsTrigger>
            <TabsTrigger value="buy" className="flex-1">
              <Plus className="size-3.5" />
              Buy New
            </TabsTrigger>
            <TabsTrigger value="custom" className="flex-1">
              <Hash className="size-3.5" />
              Custom DID
            </TabsTrigger>
          </TabsList>

          {error && (
            <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <TabsContent value="import">
            <ImportTab onSuccess={handleSuccess} onError={setError} />
          </TabsContent>
          <TabsContent value="buy">
            <BuyTab onSuccess={handleSuccess} onError={setError} />
          </TabsContent>
          <TabsContent value="custom">
            <CustomDidTab onSuccess={handleSuccess} onError={setError} />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportTab({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const q = useQuery({
    queryKey: ["phone-numbers", "existing"],
    queryFn: phoneNumbersApi.existing,
    staleTime: 30_000,
  });

  const importM = useMutation({
    mutationFn: (num: ExistingNumber) =>
      phoneNumbersApi.importNumber({ number: num.number, sid: num.sid }),
    onSuccess: () => {
      toast.success("Number imported");
      onSuccess();
    },
    onError: (err) => {
      onError(errorText(err, "Failed to import number."));
    },
  });

  const [filter, setFilter] = useState("");
  const all = q.data?.numbers ?? [];
  // Match on digits so "+1 771", "1771" and "(771)" all find +1771….
  const digits = filter.replace(/\D/g, "");
  const numbers = all.filter(
    (n) =>
      !filter.trim() ||
      (digits && n.number.replace(/\D/g, "").includes(digits)) ||
      (n.formatted_number ?? "").toLowerCase().includes(filter.trim().toLowerCase()),
  );

  return (
    <div className="space-y-2 py-2">
      {q.isLoading && (
        <div className="py-4 text-center">
          <Skeleton className="mx-auto h-8 w-3/4" />
        </div>
      )}
      {q.isError && (
        <p className="py-6 text-center text-sm text-destructive">
          {errorText(q.error, "Could not load the numbers in your Twilio account.")}
        </p>
      )}
      {q.isSuccess && all.length === 0 && (
        <p className="py-6 text-center text-sm text-muted-foreground">
          No phone numbers found in your Twilio account.
        </p>
      )}
      {all.length > 0 && (
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search numbers, e.g. +1771"
            className="pl-8"
          />
        </div>
      )}
      {all.length > 0 && numbers.length === 0 && (
        <p className="py-4 text-center text-sm text-muted-foreground">
          No numbers match “{filter}”.
        </p>
      )}
      <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
        {numbers.map((n) => (
          <button
            key={n.number}
            type="button"
            disabled={n.already_imported || importM.isPending}
            onClick={() => importM.mutate(n)}
            className={cn(
              "flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors",
              n.already_imported
                ? "opacity-60"
                : "hover:bg-accent",
            )}
          >
            <div>
              <p className="font-mono">{n.formatted_number || n.number}</p>
              <p className="text-xs text-muted-foreground">
                {n.already_imported ? "Already imported" : "Click to import"}
              </p>
            </div>
            {n.already_imported && (
              <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400">
                Imported
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function BuyTab({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const search = useMutation({
    mutationFn: () => phoneNumbersApi.search({ country_code: "US" }),
    onError: (err) => {
      onError(errorText(err, "Failed to search numbers."));
    },
  });

  const purchase = useMutation({
    mutationFn: (number: string) => phoneNumbersApi.purchase({ number }),
    onSuccess: () => {
      toast.success("Number purchased");
      onSuccess();
    },
    onError: (err) => {
      onError(errorText(err, "Failed to purchase number."));
    },
  });

  const numbers = search.data?.numbers ?? [];

  return (
    <div className="space-y-3 py-2">
      <div className="flex justify-center">
        <Button
          variant="outline"
          onClick={() => search.mutate()}
          disabled={search.isPending}
        >
          <Search className="size-4" />
          {search.isPending ? "Searching…" : "Search Available Numbers"}
        </Button>
      </div>
      {!search.isPending && search.data && numbers.length === 0 && (
        <p className="py-2 text-center text-sm text-muted-foreground">
          No available numbers found. Check your Twilio account balance.
        </p>
      )}
      <div className="space-y-2">
        {numbers.map((n) => (
          <button
            key={n.number}
            type="button"
            disabled={purchase.isPending}
            onClick={() => purchase.mutate(n.number)}
            className="flex w-full flex-col rounded-md border px-3 py-2 text-left text-sm transition-colors hover:bg-accent"
          >
            <p className="font-mono">{n.formatted_number || n.number}</p>
            <p className="text-xs text-muted-foreground">
              {n.country_code} · Voice:{" "}
              {n.capabilities?.voice ? "Yes" : "No"} · SMS:{" "}
              {n.capabilities?.sms ? "Yes" : "No"}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}

function CustomDidTab({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const [did, setDid] = useState("");
  const [label, setLabel] = useState("");
  // Outbound calls are dialled through Telnyx only for numbers marked
  // "telnyx"; anything else goes the Twilio way.
  const creds = useQuery({ queryKey: ["providers", "list"], queryFn: providersApi.list, staleTime: 30_000 });
  const hasTelnyx = (creds.data ?? []).some((c) => c.provider === "telnyx" && c.is_active);
  const [provider, setProvider] = useState<string | null>(null);
  const chosen = provider ?? (hasTelnyx ? "telnyx" : "custom");

  const create = useMutation({
    mutationFn: () =>
      phoneNumbersApi.create({
        number: did.trim(),
        formatted_number: did.trim(),
        label: label.trim(),
        provider: chosen,
        status: "active",
      }),
    onSuccess: () => {
      toast.success("Number added");
      onSuccess();
    },
    onError: (err) => {
      onError(errorText(err, "Failed to add number."));
    },
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = did.trim();
    if (!trimmed) return;
    if (!trimmed.startsWith("+")) {
      onError("Phone number must be in E.164 format (e.g. +14155551234).");
      return;
    }
    onError("");
    create.mutate();
  }

  return (
    <form onSubmit={submit} className="space-y-3 py-2">
      <p className="text-sm text-muted-foreground">
        Add a phone number (DID) from any provider — Telnyx, Vonage, or your
        own SIP carrier. You can link it to a SIP trunk for inbound routing.
      </p>
      <div className="space-y-1.5">
        <Label htmlFor="custom-did">Phone Number (E.164)</Label>
        <Input
          id="custom-did"
          value={did}
          onChange={(e) => setDid(e.target.value)}
          placeholder="+14155551234"
          className="font-mono"
          required
        />
        <p className="text-xs text-muted-foreground">
          Enter the number in E.164 format (e.g. +44xxxxxxxxxx).
        </p>
      </div>
      <div className="space-y-1.5">
        <Label>Provider</Label>
        <Select value={chosen} onValueChange={setProvider}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="telnyx">Telnyx</SelectItem>
            <SelectItem value="custom">Other (SIP carrier)</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {chosen === "telnyx"
            ? hasTelnyx
              ? "Calls from this number are placed with your Telnyx keys (Settings → API Providers)."
              : "Add your Telnyx keys in Settings → API Providers before placing calls from this number."
            : "For numbers on your own SIP trunk."}
        </p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="custom-label">Label (optional)</Label>
        <Input
          id="custom-label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. UK Support Line"
        />
      </div>
      <Button type="submit" disabled={create.isPending || !did.trim()}>
        {create.isPending ? "Adding…" : "Add Number"}
      </Button>
    </form>
  );
}

function AssignAgentDialog({
  target,
  agents,
  assistants,
  onClose,
  onSaved,
}: {
  target: PhoneNumber | null;
  agents: { id: string; name: string }[];
  assistants: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  // "agent:<id>", "assistant:<id>" or "none" — at most one answers the number.
  const [answeredBy, setAnsweredBy] = useState<string>("");
  const [provider, setProvider] = useState<string>("twilio");

  useEffect(() => {
    if (target) {
      setAnsweredBy(
        target.inbound_assistant
          ? `assistant:${target.inbound_assistant}`
          : target.inbound_agent
          ? `agent:${target.inbound_agent}`
          : "none",
      );
      setProvider(target.provider || "twilio");
    }
  }, [target]);

  const save = useMutation({
    mutationFn: () => {
      const [kind, id] = answeredBy === "none" ? [] : answeredBy.split(":");
      return phoneNumbersApi.update(target!.id, {
        inbound_agent: kind === "agent" ? id : null,
        inbound_assistant: kind === "assistant" ? id : null,
        provider,
      });
    },
    onSuccess: () => {
      toast.success("Number settings saved");
      onSaved();
      onClose();
    },
    onError: (err) => {
      toast.error(errorText(err, "Failed to update."));
    },
  });

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            Number Settings — {target?.formatted_number || target?.number}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Answered by</Label>
            <Select value={answeredBy || undefined} onValueChange={setAnsweredBy}>
              <SelectTrigger>
                <SelectValue placeholder="Select who answers this number" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nobody (outbound only)</SelectItem>
                {assistants.length > 0 && (
                  <SelectGroup>
                    <SelectLabel>Assistants</SelectLabel>
                    {assistants.map((a) => (
                      <SelectItem key={a.id} value={`assistant:${a.id}`}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
                {agents.length > 0 && (
                  <SelectGroup>
                    <SelectLabel>Agents</SelectLabel>
                    {agents.map((a) => (
                      <SelectItem key={a.id} value={`agent:${a.id}`}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              The selected agent or assistant handles all inbound calls to this
              number.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Provider</Label>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="twilio">Twilio</SelectItem>
                <SelectItem value="telnyx">Telnyx</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
                <SelectItem value="imported">Imported</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Which carrier places calls from this number. Choose{" "}
              <span className="font-medium">Telnyx</span> for a Telnyx number,
              or outbound calls go through Twilio.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WebhookInfoDialog({
  target,
  onClose,
}: {
  target: PhoneNumber | null;
  onClose: () => void;
}) {
  const base = useMemo(() => window.location.origin, []);
  const isTwilio = !target?.provider || target?.provider === "twilio";

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Webhook Setup — {target?.formatted_number || target?.number}
          </DialogTitle>
          <DialogDescription>
            Configure your provider to send call webhooks to the appropriate
            URL.
          </DialogDescription>
        </DialogHeader>

        {isTwilio && (
          <>
            <WebhookBlock
              label="Twilio — Voice Webhook URL (HTTP POST):"
              url={`${base}/api/v1/telephony/incoming/voice/`}
            />
            <WebhookBlock
              label="Twilio — Status Callback URL:"
              url={`${base}/api/v1/telephony/incoming/status/`}
            />
            <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200">
              Twilio Console → Phone Numbers → select this number → Voice &
              Fax → set the webhook URLs above.
            </div>
          </>
        )}

        {!isTwilio && (
          <>
            <WebhookBlock
              label="Telnyx — Webhook URL:"
              url={`${base}/api/v1/telephony/incoming/telnyx/`}
            />
            <WebhookBlock
              label="Vonage / Nexmo — Answer URL:"
              url={`${base}/api/v1/telephony/incoming/vonage/`}
            />
            <WebhookBlock
              label="Generic SIP / Other Providers:"
              url={`${base}/api/v1/telephony/incoming/sip/`}
            />
            <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200">
              Configure your SIP provider to send HTTP POST webhooks to the URL
              matching your provider. The generic endpoint accepts JSON with
              fields: call_id, from, to.
            </div>
          </>
        )}

        <DialogFooter>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WebhookBlock({ label, url }: { label: string; url: string }) {
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium">{label}</p>
      <div className="flex items-center gap-2">
        <pre className="flex-1 overflow-x-auto rounded-md bg-muted px-2 py-1.5 font-mono text-xs">
          {url}
        </pre>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            navigator.clipboard.writeText(url);
            toast.success("Copied");
          }}
        >
          Copy
        </Button>
      </div>
    </div>
  );
}
